import { isFramed as isFramedDefault } from './isFramed';

/** Namespace so a host can tell checkout's traffic apart from other frames'. */
export const CHECKOUT_FRAME_MESSAGE_SOURCE = 'vp-checkout';

export type CheckoutResizeMessage = {
  source: typeof CHECKOUT_FRAME_MESSAGE_SOURCE;
  type: 'resize';
  height: number;
};

export type CheckoutScrollToTopMessage = {
  source: typeof CHECKOUT_FRAME_MESSAGE_SOURCE;
  type: 'scroll-to-top';
};

export type CheckoutFrameMessage = CheckoutResizeMessage | CheckoutScrollToTopMessage;

type Scheduler = (callback: () => void) => void;

export type ParentFrameMessengerOptions = {
  win?: Window;
  isFramed?: () => boolean;
  schedule?: Scheduler;
};

export type ParentFrameMessenger = {
  postResize: (height: number) => void;
  postScrollToTop: () => void;
};

// Coalesce a burst of resizes into one post per animation frame, so a fast
// sequence of changes (Stripe mounting, validation errors, cart edits) does not
// flood the host.
const defaultSchedule: Scheduler = callback => {
  requestAnimationFrame(callback);
};

/**
 * Reports checkout's content height (and scroll-to-top requests) to the
 * embedding page. Kept framework-free so the message contract, the framed gate
 * and the coalescing are unit-testable without a DOM; the Vue wiring lives in
 * `composables/useParentFrameSync.ts`.
 *
 * `targetOrigin` is `'*'` by necessity: a framed checkout cannot know the
 * embedding origin and has no channel to be told it. Content height is not
 * sensitive, so this is an acceptable, deliberate decision -- it must NOT be
 * copied for any later message carrying sensitive data (e.g. a purchase
 * fingerprint). See packages/checkout/doc/claude/iframe-embedding.md.
 */
export const createParentFrameMessenger = (
  options: ParentFrameMessengerOptions = {},
): ParentFrameMessenger => {
  const win = options.win ?? window;
  const isFramed = options.isFramed ?? isFramedDefault;
  const schedule = options.schedule ?? defaultSchedule;

  let scheduled = false;
  let pendingHeight: number | null = null;
  let lastPostedHeight: number | null = null;

  const post = (message: CheckoutFrameMessage) => {
    // The one guarantee that nothing is posted when checkout owns the page.
    if (!isFramed()) {
      return;
    }
    win.parent.postMessage(message, '*');
  };

  const flush = () => {
    scheduled = false;
    if (pendingHeight === null || pendingHeight === lastPostedHeight) {
      return;
    }
    lastPostedHeight = pendingHeight;
    post({ source: CHECKOUT_FRAME_MESSAGE_SOURCE, type: 'resize', height: pendingHeight });
  };

  const postResize = (height: number) => {
    pendingHeight = height;
    if (scheduled) {
      return;
    }
    scheduled = true;
    schedule(flush);
  };

  const postScrollToTop = () => {
    post({ source: CHECKOUT_FRAME_MESSAGE_SOURCE, type: 'scroll-to-top' });
  };

  return { postResize, postScrollToTop };
};
